import { useState, useEffect } from 'react';
import { Sparkles } from 'lucide-react';
import { getAIUsage, AIUsageDetails } from '../lib/aiUsage';
import { useAuth } from '../lib/AuthContext';
import { useTranslation } from '../lib/LanguageContext';

interface AIUsageIndicatorProps {
  onUpgrade?: () => void;
}

export function AIUsageIndicator({ onUpgrade }: AIUsageIndicatorProps) {
  const [usage, setUsage] = useState<AIUsageDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { t } = useTranslation();

  const fetchUsage = async () => {
    try {
      setLoading(true);
      console.log('AIUsageIndicator: Fetching AI usage');
      const data = await getAIUsage();
      console.log('AIUsageIndicator: Usage data received', data);
      setUsage(data);
    } catch (error) {
      console.error('AIUsageIndicator: Error fetching AI usage:', error);
      // Set default values in case of error
      setUsage({
        usageCount: 0,
        usageLimit: 10,
        isWithinLimit: true,
        remainingUsage: 10
      });
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch when component mounts
  useEffect(() => {
    if (user) {
      fetchUsage();
    }
  }, [user]);

  // Listen for AI usage updates
  useEffect(() => {
    const handleAIUsageUpdated = () => {
      console.log('AIUsageIndicator: Received aiUsageUpdated event');
      if (user) {
        fetchUsage();
      }
    };

    window.addEventListener('aiUsageUpdated', handleAIUsageUpdated);
    
    return () => {
      window.removeEventListener('aiUsageUpdated', handleAIUsageUpdated);
    };
  }, [user]);

  // Force refresh every minute to keep the display up to date
  useEffect(() => {
    const interval = setInterval(() => {
      if (user) {
        fetchUsage();
      }
    }, 60000);
    
    return () => clearInterval(interval);
  }, [user]);

  if (loading && !usage) {
    console.log('AIUsageIndicator: Still loading or no usage data');
    return null;
  }

  if (!usage) {
    console.log('AIUsageIndicator: No usage data available');
    return null;
  }

  const percentUsed = Math.round((usage.usageCount / usage.usageLimit) * 100);
  const isLowUsage = percentUsed < 70;
  const isMediumUsage = percentUsed >= 70 && percentUsed < 90;
  const isHighUsage = percentUsed >= 90;

  console.log('AIUsageIndicator: Rendering with data', {
    usageCount: usage.usageCount,
    usageLimit: usage.usageLimit,
    percentUsed,
    isWithinLimit: usage.isWithinLimit
  });

  return (
    <div className="flex items-center space-x-2 text-sm">
      <div className="flex items-center">
        <Sparkles className="h-4 w-4 text-indigo-400 mr-1" />
        <span className="text-indigo-100">
          {t('aiSuggestions', { count: usage.usageCount, limit: usage.usageLimit })}
        </span>
      </div>
      
      <div className="w-16 bg-gray-700 rounded-full h-1.5">
        <div 
          className={`h-1.5 rounded-full ${
            isLowUsage ? 'bg-green-500' : 
            isMediumUsage ? 'bg-yellow-500' : 
            'bg-red-500'
          }`} 
          style={{ width: `${percentUsed}%` }}
        ></div>
      </div>
      
      {isHighUsage && onUpgrade && (
        <button
          onClick={onUpgrade}
          className="text-xs bg-indigo-700 hover:bg-indigo-600 text-white px-2 py-0.5 rounded"
        >
          {t('upgrade')}
        </button>
      )}
    </div>
  );
}